import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  MenuItem,
  Stack,
  Typography,
} from "@mui/material";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import { useRef, useState } from "react";
import LanguageTabs from "../LanguageTabs";
import { EditDrawer } from "../EditDrawer";
import {
  IMAGE_TYPES,
  IMAGE_TYPE_SPECS,
  imageSrc,
  ARTWORK_ACCEPT,
  type ImageType,
} from "@shared/vod-images";
import { canonicalAssetLanguage } from "@shared/vod-title";
import {
  assetToDraft,
  emptyImageDraft,
  type ImageDraft,
} from "@shared/asset-draft";
import { foyerApi, type VodTitleRow } from "../../http/foyer";
import { describeError, useToast } from "../toast";
import { useTranslation } from "react-i18next";
import { translateKey } from "../../i18n/translate";
import { measureImage } from "../../lib/imageSize";

import type { LanguageStatus } from "@shared/vod-localised";
import { tokens } from "../../theme";
import { CollectionRow, Field } from "./editorBits";

/**
 * "Localised images" - the panel that edits `images[]` (`BE-9`, re-pointed by
 * `BE-15`).
 *
 * ## Why this is a LIST and not a grid
 *
 * The shipped mockup drew `images[language][kind]` - four kinds, one slot each.
 * Two things are wrong with that and both cost data:
 *
 *  - there are **six** types, not four (`logo` and `cover` had no slot at all,
 *    and `logo` is most of what a channel carries - so a channel logo was
 *    invisible to every count and every resolver in Foyer);
 *  - **nothing on the `Image` table is unique.** The whole index list is
 *    `PRIMARY KEY(imageId)`, so `(assetId, language, type)` may repeat freely.
 *    A map keyed by the pair can only ever keep the last one, which means
 *    opening this panel over a real title and saving would silently delete its
 *    siblings.
 *
 * So: a list, grouped by language for navigation only, with an explicit Add.
 *
 * ## `imageId` is what says "keep this row"
 *
 * A row carrying one is an UPDATE of that row; a row without one is a new
 * image. Dropping ids on save would delete and recreate every image in the
 * catalogue on every edit - and upstream updates by id with **no ownership
 * check**, so a restated id belonging to another asset overwrites that asset's
 * row in place. The server checks ids against what the asset actually owns;
 * nothing here invents one.
 *
 * ## What this panel deliberately does NOT offer
 *
 * **A picker over Foyer's media library.** Foyer's asset store and the Content
 * API's image container are different stores: `Image.url` is a RELATIVE blob
 * path in the catalogue's own container, and the device path builds
 * `${imageBase}/${url}` from it. Putting a Foyer URL in there would draw
 * correctly in this CMS and 404 on every television - the worst possible split.
 *
 * ## Uploading lives HERE now, and the storage-path field is gone
 *
 * `ART-2` put an upload on the wizard's artwork step and left this panel as
 * "the tool for a picture that is ALREADY in the container - a path typed by
 * somebody who knows the container". That split asked the operator to hold two
 * ideas: a button that adds a picture, and a list whose rows are filled in by
 * typing a blob path.
 *
 * **Nobody outside Nenda can type that path.** It is a relative key into a
 * container a supplier has no access to and no way to enumerate, so the field
 * was answerable only by the handful of people who did not need the panel. In
 * exchange it offered every operator a free way to write a path that resolves
 * to nothing — well-formed, saved without complaint, and a broken picture on
 * every television.
 *
 * So the field is gone and the PREVIEW SQUARE is the control: empty means
 * "click to add", filled means "click to replace". A row's picture can now only
 * arrive by uploading bytes, which is the one way that cannot name a blob that
 * is not there.
 *
 * ⚠️ **An upload is immediate; everything else waits for Save.** The bytes and
 * the row are written by `POST …/images` the moment a file is chosen — the row
 * has to exist before it can be named — while type, language and dimensions are
 * ordinary draft edits. Cancel therefore discards the edits and NOT the upload,
 * which is why the panel says so out loud rather than letting somebody discover
 * it.
 *
 * ⚠️ **A row that already has an `imageId` REPLACES** (`?replace=`), it does not
 * append. On a table with no unique key, appending where the operator asked for
 * a replacement silently doubles the artwork and nothing downstream notices.
 */

const PREVIEW_WIDTH = 120;
const PREVIEW_HEIGHT = 72;

/**
 * What a row an OPERATOR just made starts as.
 *
 * Deliberately NOT `DEFAULT_IMAGE_TYPE`. That constant is `other`, and it is
 * right where it is used — reading a catalogue row whose `type` is absent,
 * where "other" is the honest answer and a guess would be a rewrite. A row
 * somebody just created by clicking an empty square is the opposite situation:
 * there is no stored value to respect, and `poster` is what the picker this
 * replaced always sent.
 *
 * It also matters for data quality rather than convenience. `other` is one of
 * the open counting problems in [[catalogue-data-quality]] — rows nothing can
 * place — and defaulting every new picture into it would grow that pile one
 * upload at a time, from a dropdown most operators would never think to touch.
 */
const NEW_ROW_TYPE: ImageType = "poster";

/** A blank row for `language`, typed the way an operator means it. */
function newRow(language: string): ImageDraft {
  return { ...emptyImageDraft(language), type: NEW_ROW_TYPE };
}

export default function LocalisedImagesPanel({
  open,
  images,
  assetId,
  surface,
  onClose,
  onSave,
  onUploaded,
}: {
  open: boolean;
  images: ImageDraft[];
  /** The title the bytes are filed against. Empty disables uploading. */
  assetId: string;
  /**
   * Which route set to speak to. Bound by the caller, never sniffed here: a
   * supplier call written against the staff URL does not fail loudly, it 403s
   * somewhere the shell cannot explain. Same reason `ingestCalls(area)` binds
   * its base once.
   */
  surface: "staff" | "owner";
  onClose: () => void;
  onSave: (next: ImageDraft[]) => void;
  /**
   * The WHOLE updated title, straight from the upload response, so a caller
   * holding its own copy can re-seed rather than guess what the row now says.
   */
  onUploaded?: (title: VodTitleRow) => void;
}) {
  const toast = useToast();
  const { t } = useTranslation(["content", "common"]);
  const [draft, setDraft] = useState<ImageDraft[]>(images);
  const [language, setLanguage] = useState(images[0]?.language ?? "sv");
  const [uploading, setUploading] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const pick = useRef<HTMLInputElement | null>(null);
  const pickingFor = useRef<number | null>(null);

  // See the sibling panel: mounted by a dialog that stays mounted, so it
  // reseeds on open rather than being remounted by a key it cannot see.
  const [seeded, setSeeded] = useState(false);
  if (open && !seeded) {
    setDraft(images);
    setLanguage(images[0]?.language ?? "sv");
    setSeeded(true);
  }
  if (!open && seeded) setSeeded(false);

  const languages = [...new Set(draft.map((image) => image.language))];
  const shown = draft
    .map((image, index) => ({ image, index }))
    .filter((entry) => entry.image.language === language);

  /**
   * A language always shows at least one square, because the square is the
   * control and "no artwork in SV yet" is a sentence with nothing to click.
   *
   * Set during render, like the seeding above it and for the same reason — the
   * dialog stays mounted, so there is no mount to hang an effect on. It cannot
   * loop: the appended row carries `language`, so the next render finds it.
   *
   * ⚠️ Safe ONLY because `onSave` drops empty rows. Without that this would
   * write a row with an empty url every time somebody opened a language and
   * looked at it — see the filter in the save handler.
   */
  if (open && shown.length === 0) {
    setDraft((prev) => [...prev, newRow(language)]);
  }

  // A language is "done" once it carries at least one image with a url, and
  // "partial" while every row it has is empty. Same three states as the text
  // panel, from the same reasoning: not started and abandoned halfway are
  // different problems.
  const statuses: Record<string, LanguageStatus> = Object.fromEntries(
    languages.map((code) => {
      const rows = draft.filter((image) => image.language === code);
      const filled = rows.filter((image) => image.url.trim() !== "").length;
      return [code, filled ? "done" : rows.length ? "partial" : "missing"];
    })
  );

  function update(index: number, patch: Partial<ImageDraft>) {
    setDraft((prev) =>
      prev.map((image, i) => (i === index ? { ...image, ...patch } : image))
    );
  }

  /**
   * Put bytes on the row at `index`.
   *
   * ## Why the result is MERGED rather than re-seeded
   *
   * The obvious move is to take the title that comes back and replace the whole
   * draft with it. That throws away every unsaved edit in the panel — a type
   * changed, a language added, three rows removed — because an upload is now
   * something an operator does in the MIDDLE of editing rather than a separate
   * trip. So only the row that was written is touched, and only in the fields
   * the server actually decided.
   *
   * ## Finding the row the server wrote
   *
   * A replace is identified by the `imageId` we sent. An append is not
   * identified by anything, so it is found by difference: the one id in the
   * response that this draft has never seen. `Image` has no unique key on
   * `(language, type)`, so matching on those instead would pick the wrong row
   * the moment a language carries two posters — which is exactly the shape this
   * panel exists to support.
   */
  async function uploadInto(index: number, file: File) {
    const row = draft[index];
    if (!row || !assetId) return;
    setUploading(index);
    setUploadError(null);
    try {
      // The browser measures it; the server has no decoder and never will. An
      // unmeasurable file still uploads — `0 x 0` is legal upstream.
      const measured = await measureImage(file);
      const call =
        surface === "owner"
          ? foyerApi.uploadOwnTitleImage
          : foyerApi.uploadVodTitleImage;
      const saved = await call(assetId, file, {
        language: row.language,
        type: row.type,
        width: measured?.width ?? null,
        height: measured?.height ?? null,
        // The wire carries it as a query string; the draft holds the
        // catalogue's own numeric id.
        replace: row.imageId === null ? null : String(row.imageId),
      });

      const known = new Set(
        draft
          .map((image) => image.imageId)
          .filter((id): id is number => id !== null)
      );
      const returned = assetToDraft(saved).images;
      const written = row.imageId
        ? returned.find((image) => image.imageId === row.imageId)
        : returned.find(
            (image) => image.imageId !== null && !known.has(image.imageId)
          );

      if (written) {
        update(index, {
          imageId: written.imageId,
          url: written.url,
          width: written.width,
          height: written.height,
          source: written.source,
        });
      }
      toast.success(
        t("localisedImagesPanel.uploaded", {
          name: file.name,
          language: row.language,
        })
      );
      onUploaded?.(saved);
    } catch (e) {
      const err = e as { code?: string; message?: string };
      toast.error(describeError(t, "uploadPicture", e, { name: file.name }));
      setUploadError(
        err.code === "unsupported_image_type" || err.code === "image_too_large"
          ? t(`localisedImagesPanel.errors.${err.code}`)
          : // ⚠️ The server's own sentence, not a stand-in for it. Both of
            // these name a CONFIGURATION fault and say which one — "point
            // CONTENT_API_IMAGE_BASE_URL and the artwork credential at the
            // same environment" is actionable, where "storage is not switched
            // on" sends somebody hunting for a missing credential that is
            // already there. Discarding it cost an afternoon on 2026-08-28.
            (err.message ?? t("localisedImagesPanel.couldNotUpload"))
      );
    } finally {
      setUploading(null);
    }
  }

  function addLanguage(code: string) {
    const canonical = canonicalAssetLanguage(code);
    if (!canonical) return;
    setLanguage(canonical);
    setDraft((prev) =>
      prev.some((image) => image.language === canonical)
        ? prev
        : [...prev, newRow(canonical)]
    );
  }

  return (
    <EditDrawer
      open={open}
      onClose={onClose}
      title={t("localisedImagesPanel.title")}
      subtitle={t("localisedImagesPanel.subtitle")}
      data-testid="manage-localised-images"
      actions={(dismiss) => (
        <>
          <Button variant="outlined" onClick={() => dismiss()}>
            {t("common:buttons.cancel")}
          </Button>
          <Button
            variant="contained"
            /**
             * ⚠️ Empty rows are DROPPED, never saved.
             *
             * `imagePayload` does not filter, so a row with no url went
             * upstream as `url: ""` — an image that draws nothing, counts as
             * one in every total, and can only be removed by finding it again.
             * "Add image +" and then changing your mind was enough to make one,
             * which is why this is a fix and not just support for the
             * always-one-square rule above.
             *
             * A row with an `imageId` is kept whatever its url: that is a real
             * catalogue row, and dropping it here would silently DELETE it on
             * the next save rather than leave it alone.
             *
             * `onSave` is called straight away, not through `dismiss`: every
             * caller keeps this drawer mounted and answers by flipping `open`
             * to false, which plays the slide-out on its own.
             */
            onClick={() =>
              onSave(
                draft.filter(
                  (image) => image.url.trim() !== "" || image.imageId !== null
                )
              )
            }
            data-testid="images-save"
          >
            {t("localisedImagesPanel.saveImages")}
          </Button>
        </>
      )}
    >
      <Stack>
        <LanguageTabs
          languages={languages}
          value={language}
          statuses={statuses}
          onChange={setLanguage}
          onAdd={addLanguage}
        />

        {/**
         * One input for the whole panel, with the row it was opened for held in
         * a ref. One per row would mount a file input per image for the sake of
         * a value that is read once.
         *
         * The value is cleared on change so choosing the SAME file twice fires
         * again — otherwise a failed upload cannot be retried without picking
         * something else first, which is the first thing anybody tries.
         */}
        <input
          ref={pick}
          type="file"
          accept={ARTWORK_ACCEPT}
          hidden
          data-testid="images-file-input"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            const index = pickingFor.current;
            pickingFor.current = null;
            if (file && index !== null) void uploadInto(index, file);
          }}
        />

        {uploadError && (
          <Alert
            severity="error"
            sx={{ mb: 1 }}
            onClose={() => setUploadError(null)}
            data-testid="images-upload-error"
          >
            {uploadError}
          </Alert>
        )}

        {/* No inner scroll box: the drawer body scrolls. */}
        <Stack spacing={2}>
          {shown.map(({ image, index }) => {
            const preview = imageSrc(image, { width: PREVIEW_WIDTH });
            const spec = IMAGE_TYPE_SPECS[image.type];
            return (
              <CollectionRow
                key={image.imageId ?? `new-${index}`}
                title={
                  image.imageId === null
                    ? t("localisedImagesPanel.newImage")
                    : t("localisedImagesPanel.imageId", { id: image.imageId })
                }
                removeLabel={t("common:buttons.remove")}
                onRemove={() =>
                  setDraft((prev) => prev.filter((_, i) => i !== index))
                }
              >
                <Stack direction="row" spacing={2}>
                  {/**
                   * The square IS the control. Empty it invites a file; filled
                   * it replaces one. A separate "upload" button beside a
                   * picture would leave the picture looking like decoration,
                   * which is what it was while the path was typed by hand.
                   */}
                  <ButtonBase
                    disabled={!assetId || uploading !== null}
                    onClick={() => {
                      pickingFor.current = index;
                      pick.current?.click();
                    }}
                    aria-label={t(
                      image.url.trim()
                        ? "localisedImagesPanel.replacePicture"
                        : "localisedImagesPanel.addPicture",
                      {
                        type: t(
                          `localisedImagesPanel.imageType.${image.type}.label`
                        ),
                      }
                    )}
                    data-testid={`image-square-${index}`}
                    sx={{
                      width: PREVIEW_WIDTH,
                      height: PREVIEW_HEIGHT,
                      flex: "0 0 auto",
                      borderRadius: 1,
                      border: `1px ${preview ? "solid" : "dashed"} ${tokens.divider}`,
                      bgcolor: tokens.brandTintSoft,
                      backgroundImage: preview ? `url(${preview})` : undefined,
                      backgroundSize: "cover",
                      backgroundPosition: "center",
                      color: tokens.inkMuted,
                      // The hover affordance has to survive a dark poster
                      // behind it, so it dims the picture rather than tinting.
                      "&:hover .overlay": { opacity: 1 },
                      "&.Mui-disabled": { opacity: 0.6 },
                    }}
                  >
                    {uploading === index ? (
                      <CircularProgress size={20} />
                    ) : preview ? (
                      <Box
                        className="overlay"
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: 1,
                          opacity: 0,
                          transition: "opacity 120ms",
                          bgcolor: "rgba(0,0,0,0.55)",
                          color: "#fff",
                          typography: "meta",
                          fontWeight: "fontWeightSemibold",
                        }}
                      >
                        {t("localisedImagesPanel.replace")}
                      </Box>
                    ) : (
                      <Stack alignItems="center" spacing={0.25}>
                        <AddPhotoAlternateOutlinedIcon fontSize="small" />
                        <Box sx={{ typography: "micro" }}>
                          {t("common:buttons.add")}
                        </Box>
                      </Stack>
                    )}
                  </ButtonBase>
                  <Stack spacing={1.5} sx={{ flex: "1 1 auto", minWidth: 0 }}>
                    <Stack direction="row" spacing={1.5}>
                      <Field
                        select
                        label={t("common:labels.type")}
                        value={image.type}
                        onChange={(e) =>
                          update(index, { type: e.target.value as ImageType })
                        }
                        sx={{ width: 160 }}
                      >
                        {IMAGE_TYPES.map((type) => (
                          <MenuItem key={type} value={type}>
                            {t(`localisedImagesPanel.imageType.${type}.label`)}
                          </MenuItem>
                        ))}
                      </Field>
                      <Field
                        label={t("localisedImagesPanel.width")}
                        value={image.width}
                        onChange={(e) =>
                          update(index, { width: e.target.value })
                        }
                        sx={{ width: 96 }}
                      />
                      <Field
                        label={t("localisedImagesPanel.height")}
                        value={image.height}
                        onChange={(e) =>
                          update(index, { height: e.target.value })
                        }
                        sx={{ width: 96 }}
                      />
                    </Stack>
                    {/**
                     * What the shape should be, where the storage path used to
                     * be. The spec was only ever visible as this field's helper
                     * text, and it is the half of it worth keeping: it tells an
                     * operator which picture to go and find.
                     *
                     * The broken-path warning is gone with the field — a url
                     * can no longer be typed, so it can no longer be typed
                     * wrong. A row whose stored path predates this and does not
                     * resolve still shows an empty square, which now reads as
                     * "click to fix" rather than as a mystery.
                     */}
                    <Typography variant="meta" sx={{ color: tokens.inkMuted }}>
                      {spec.note
                        ? t("localisedImagesPanel.specLine", {
                            shape: t(
                              `localisedImagesPanel.imageType.${image.type}.shape`
                            ),
                            note: translateKey(
                              t,
                              `localisedImagesPanel.imageType.${image.type}.note`
                            ),
                          })
                        : t(
                            `localisedImagesPanel.imageType.${image.type}.shape`
                          )}
                    </Typography>
                  </Stack>
                </Stack>
              </CollectionRow>
            );
          })}

          <Button
            size="small"
            variant="outlined"
            sx={{ alignSelf: "flex-start" }}
            data-testid="image-add"
            onClick={() => setDraft((prev) => [...prev, newRow(language)])}
          >
            {t("localisedImagesPanel.addImage")}
          </Button>
        </Stack>
      </Stack>
    </EditDrawer>
  );
}
